import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, request } from '../lib/api-client.ts';
import type { AnalyzeScoreResult } from '../types/analyze.ts';

type Props = {
  onAnalyzed: () => void;
};

type RunStatus =
  | { kind: 'idle' }
  | { kind: 'confirming'; mode: 'recent' | 'full' }
  | { kind: 'running' }
  | { kind: 'success'; data: AnalyzeScoreResult }
  | { kind: 'error'; message: string };

const CONFIRM_AUTO_RESET_MS = 6000;
const RESULT_AUTO_RESET_MS = 8000;

/**
 * 統合スコア計算（/api/analyze/score）の実行パネル。
 *
 * - 差分実行（直近24h で signals が更新された業種のみ）
 * - 全件実行（active な全業種を再計算）
 * いずれもインライン2段階ボタン（収集系と同じパターン）。
 */
export const AnalyzePanel = ({ onAnalyzed }: Props): React.JSX.Element => {
  const [status, setStatus] = useState<RunStatus>({ kind: 'idle' });
  const mountedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const scheduleReset = (delay: number): void => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (mountedRef.current) setStatus({ kind: 'idle' });
    }, delay);
  };

  const startConfirm = (mode: 'recent' | 'full'): void => {
    setStatus({ kind: 'confirming', mode });
    scheduleReset(CONFIRM_AUTO_RESET_MS);
  };

  const runAnalyze = async (mode: 'recent' | 'full'): Promise<void> => {
    setStatus({ kind: 'running' });
    const path = mode === 'full' ? '/api/analyze/score?full=true' : '/api/analyze/score';
    try {
      const data = await request<AnalyzeScoreResult>(path, { method: 'POST' });
      if (!mountedRef.current) return;
      setStatus({ kind: 'success', data });
      onAnalyzed();
      scheduleReset(RESULT_AUTO_RESET_MS);
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err instanceof ApiError ? err.message : '実行に失敗しました';
      setStatus({ kind: 'error', message });
      scheduleReset(RESULT_AUTO_RESET_MS);
    }
  };

  return (
    <section style={sectionStyle}>
      <h2 style={headStyle}>統合スコア計算</h2>
      <div style={bodyStyle}>
        <p style={descStyle}>
          シグナルを集約して統合スコアを計算し、`scores` テーブルへ書き込む（algo_version=v1.0）。
        </p>
        <div style={actionRowStyle}>
          {renderButtons(status, startConfirm, runAnalyze)}
        </div>
        {status.kind === 'success' && <ResultLine data={status.data} />}
        {status.kind === 'error' && <span style={errorStyle}>{status.message}</span>}
      </div>
    </section>
  );
};

const renderButtons = (
  status: RunStatus,
  startConfirm: (mode: 'recent' | 'full') => void,
  runAnalyze: (mode: 'recent' | 'full') => void
): React.JSX.Element => {
  if (status.kind === 'running') return <span>実行中…</span>;
  if (status.kind === 'confirming') {
    return (
      <button type="button" onClick={() => runAnalyze(status.mode)} style={dangerBtnStyle}>
        本当に実行（{status.mode === 'full' ? '全件' : '差分'}）
      </button>
    );
  }
  return (
    <>
      <button type="button" onClick={() => startConfirm('recent')} style={primaryBtnStyle}>
        差分実行（直近24h）
      </button>
      <button type="button" onClick={() => startConfirm('full')} style={primaryBtnStyle}>
        全件実行
      </button>
    </>
  );
};

const ResultLine = ({ data }: { data: AnalyzeScoreResult }): React.JSX.Element => (
  <div style={resultStyle}>
    <span style={resultMetricStyle}>mode: {data.mode}</span>
    <span style={resultMetricStyle}>業種: {data.industries_evaluated}</span>
    <span style={resultMetricStyle}>scores 投入: {data.scores_inserted}</span>
    {data.real_trend_count !== undefined && (
      <span style={resultMetricStyle}>本物のトレンド: {data.real_trend_count}</span>
    )}
    {data.gap_chance_count !== undefined && (
      <span style={resultMetricStyle}>ギャップ検知: {data.gap_chance_count}</span>
    )}
    <span style={resultMetricStyle}>algo: {data.algo_version}</span>
  </div>
);

const sectionStyle: React.CSSProperties = {
  width: '100%',
  marginBottom: 32,
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  background: '#fff',
};
const headStyle: React.CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  fontSize: 16,
  borderBottom: '1px solid #eee',
  background: '#f7f7f7',
};
const bodyStyle: React.CSSProperties = { padding: '16px' };
const descStyle: React.CSSProperties = { color: '#555', fontSize: 13, margin: '0 0 12px' };
const actionRowStyle: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center' };
const resultStyle: React.CSSProperties = {
  marginTop: 12,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  fontSize: 13,
  color: '#27ae60',
};
const resultMetricStyle: React.CSSProperties = {
  background: '#eafaf1',
  padding: '4px 10px',
  borderRadius: 4,
};
const errorStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 12,
  color: '#c0392b',
  fontSize: 13,
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: 13,
  cursor: 'pointer',
};
const dangerBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: 13,
  background: '#e74c3c',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};
